/* eslint-disable no-console */
import { signLogPayload } from '@ads-bread/shared/bread/util';
import type { LoggerClient } from './client';
import { isVerboseSuppressed, parseStackFrames } from './format';

type LogLevel = 'error' | 'warn' | 'info' | 'debug';

const REMOTE_LOG_ENDPOINT = '/api/log';

/**
 * Read the shared signing secret from the server-rendered app config
 * (__NEXT_DATA__.props.config.eventCollectorLogSecret). Same source used by
 * the legacy /log path and by pino.ts. Cached because __NEXT_DATA__ is
 * server-rendered and immutable for the page's lifetime.
 *
 * Returns '' when the tag is missing or the config field is unset (local
 * dev without wheat config, tests, or SSR pass). '' cleanly skips signing.
 */
let cachedSigningSecret: string | null = null;
function getSigningSecret(): string {
  if (cachedSigningSecret !== null) {
    return cachedSigningSecret;
  }
  try {
    if (typeof document !== 'undefined') {
      const tag = document.getElementById('__NEXT_DATA__');
      if (tag && tag.textContent) {
        const parsed = JSON.parse(tag.textContent);
        const secret = parsed?.props?.config?.eventCollectorLogSecret;
        if (typeof secret === 'string' && secret.length > 0) {
          cachedSigningSecret = secret;
          return cachedSigningSecret;
        }
      }
    }
  } catch {
    /* intentional: ignore parse / DOM errors — treat as no secret */
  }
  cachedSigningSecret = '';
  return cachedSigningSecret;
}

/** Reset cached secret — for test isolation only. */
export function __resetSigningSecretForTests(): void {
  cachedSigningSecret = null;
}

/**
 * The original browser `console` methods, captured before any monkey-patching
 * so downstream logger clients can still call the real console without
 * triggering the mirror (which would double-forward pino records).
 */
const noop = (): void => undefined;

export const originalConsole: Record<LogLevel, (...args: unknown[]) => void> = {
  error: typeof console === 'undefined' ? noop : console.error.bind(console),
  warn: typeof console === 'undefined' ? noop : console.warn.bind(console),
  info: typeof console === 'undefined' ? noop : console.info.bind(console),
  debug: typeof console === 'undefined' ? noop : console.debug.bind(console),
};

function safeStringify(v: unknown): string {
  if (typeof v === 'string') {
    return v;
  }
  if (v instanceof Error) {
    return v.message;
  }
  try {
    return JSON.stringify(v);
  } catch {
    /* intentional: fall back to String() when JSON.stringify throws (e.g. circular refs) */
    return String(v);
  }
}

/**
 * Last observed `x-trace-id` response header from any fetch made by the
 * browser. Used to stamp forwarded browser logs with the same trace ID as
 * the server request that triggered them. Installed by `installFetchTraceCapture()`.
 */
let lastTraceID: string | null = null;
let fetchCaptureInstalled = false;

export function installFetchTraceCapture(): void {
  if (fetchCaptureInstalled || typeof window === 'undefined' || !window.fetch) {
    return;
  }
  fetchCaptureInstalled = true;
  const origFetch = window.fetch.bind(window);
  window.fetch = async (
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> => {
    const response = await origFetch(input, init);
    try {
      const trace = response.headers.get('x-trace-id');
      if (trace) {
        lastTraceID = trace;
      }
    } catch {
      /* intentional: ignore header read failures */
    }
    return response;
  };
}

function getCurrentTraceID(): string | undefined {
  return lastTraceID || undefined;
}

/** Current browser viewport as `<width>x<height>`, or undefined server-side. */
function getViewport(): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  const w = window.innerWidth;
  const h = window.innerHeight;
  return w && h ? `${w}x${h}` : undefined;
}

// Set NEXT_PUBLIC_LOG_CALLER=false to skip stack inspection in high-throughput environments.
const BROWSER_CALLER_ENABLED = process.env.NEXT_PUBLIC_LOG_CALLER !== 'false';

// Frames we NEVER want to report — they're logger plumbing.
const LOGGER_SKIP = [
  '/lib/logger/',
  '/logger/remote',
  '/logger/pino',
  '/logger/console',
  '/logger/format',
  '/logger/splunk',
];

// Frames skipped on the first pass; allowed through on the second as a fallback.
const THIRD_PARTY_SKIP = [
  '/node_modules/',
  '/_next/static/',
  '.hot-update.',
  '/pages-dir-node/',
  '/react-refresh',
];

function getCallerFileSource(): string | undefined {
  if (!BROWSER_CALLER_ENABLED) {
    return undefined;
  }
  const stack = new Error('log-caller').stack;
  if (!stack) {
    return undefined;
  }

  const isLoggerFrame = (f: string): boolean =>
    LOGGER_SKIP.some((p) => f.includes(p));
  const isThirdParty = (f: string): boolean =>
    THIRD_PARTY_SKIP.some((p) => f.includes(p));

  const clean = (file: string, line: string): string => {
    const cleaned = file
      .replace(/^webpack-internal:\/\/\/(\(.*?\)\/)?/, '')
      .replace(/^webpack:\/\/(_N_E\/)?/, '')
      .replace(/^https?:\/\/[^/]+/, '')
      .replace(/\?.*$/, '');
    return `${cleaned}:${line}`;
  };

  const frames = parseStackFrames(stack);

  // First pass: prefer real app source paths.
  for (const { file, line } of frames) {
    if (isLoggerFrame(file) || isThirdParty(file)) {
      continue;
    }
    return clean(file, line);
  }

  // Second pass: fall back to anything that isn't our own logger machinery.
  for (const { file, line } of frames) {
    if (isLoggerFrame(file)) {
      continue;
    }
    return clean(file, line);
  }

  return undefined;
}

/**
 * Ship one browser log to the Next.js API route so it can be re-emitted by
 * the server-side pino logger (and therefore show up in the `nx serve`
 * terminal). Sends a signed request via `fetch` with `keepalive: true` so
 * it survives page unloads.
 */
async function send(
  level: LogLevel,
  message: string,
  ctx: unknown
): Promise<void> {
  if (isVerboseSuppressed(level)) {
    return;
  }
  const currentTraceID = getCurrentTraceID();
  const viewport = getViewport();
  const payload = JSON.stringify({
    level,
    message,
    ctx,
    origin:
      typeof window !== 'undefined' && window.location
        ? window.location.href
        : undefined,
    traceID: currentTraceID,
    Path: getCallerFileSource(),
  });

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (currentTraceID) {
    headers['x-request-id'] = currentTraceID;
  }
  if (viewport) {
    headers['x-viewport'] = viewport;
  }

  // Sign the payload when the shared secret is configured (raises bar against
  // log stuffing). Secret comes from __NEXT_DATA__.props.config.eventCollectorLogSecret,
  // the same source as the legacy /log path. The key is delivered via
  // server-rendered __NEXT_DATA__ so it CAN be rotated without a rebuild
  // (Helm config change + pod restart).
  const secret = getSigningSecret();
  if (secret) {
    const timestamp = Date.now().toString();
    const { signature } = await signLogPayload(payload, timestamp, secret);
    headers['x-log-timestamp'] = timestamp;
    headers['x-log-signature'] = signature;
  }

  try {
    void fetch(REMOTE_LOG_ENDPOINT, {
      method: 'POST',
      keepalive: true,
      headers,
      body: payload,
    }).catch(() => {
      /* swallow — remote logging must never break the app */
    });
  } catch {
    /* intentional: never let the logger throw */
  }
}

/**
 * A LoggerClient that forwards every browser log call to the server. Enable
 * only in development (or when explicitly opted-in) to avoid duplicating
 * production traffic (Splunk already receives prod logs).
 */
export function createRemoteLogger(enabled: boolean): LoggerClient | null {
  if (!enabled) {
    return null;
  }
  const dispatch = (level: LogLevel) => (msg: string, ctx: unknown) => {
    void send(level, msg, ctx);
  };
  return {
    error: dispatch('error'),
    warn: dispatch('warn'),
    info: dispatch('info'),
    debug: dispatch('debug'),
  };
}

/**
 * Inspect the current stack and return true if the *nearest non-logger*
 * caller is inside our own app source (not `node_modules`, not a third-party
 * runtime script). Uses a whitelist because Next.js dev shows node_modules
 * code as bundled runtime filenames like `index.es.js` (no `node_modules/`
 * segment in the path), so a blacklist approach misses them.
 *
 * A caller counts as "app code" when the frame's file path:
 *   - contains `/apps/apply/` (webpack dev source-map paths), or
 *   - looks like a bare `.ts` / `.tsx` file we author (excluding common
 *     compiled bundle suffixes: `.esm.js`, `.chunk.js`, `.hot-update.js`).
 */
function callerIsAppCode(): boolean {
  if (!BROWSER_CALLER_ENABLED) {
    return true;
  }
  const stack = new Error('caller-check').stack;
  if (!stack) {
    return true;
  }
  for (const { file } of parseStackFrames(stack)) {
    if (
      file.includes('/lib/logger/') ||
      file.includes('/logger/remote') ||
      file.includes('/logger/pino') ||
      file.includes('/logger/console')
    ) {
      continue;
    }
    if (file.includes('/apps/apply/')) {
      return true;
    }
    if (/node_modules/.test(file)) {
      return false;
    }
    if (
      /\.[tj]sx?$/.test(file) &&
      !/\.(esm|chunk|hot-update|min)\.js$/.test(file)
    ) {
      return true;
    }
    return false;
  }
  return true;
}

/**
 * Monkey-patch `window.console.{error,warn,info,debug}` so every direct
 * console call in the browser is *also* POSTed to `/api/log` (and therefore
 * shows up in the `nx serve` terminal). Idempotent — safe to call multiple
 * times.
 *
 * Logs originating from `node_modules` are printed to DevTools but NOT
 * forwarded to the terminal, to keep server-side output focused on your
 * own application code. Set NEXT_PUBLIC_MIRROR_NODE_MODULES=true to include
 * them.
 *
 * pino's own `console` client uses `originalConsole` above, so pino records
 * are NOT double-forwarded (they are already sent by the pino remote client).
 */
let mirrorInstalled = false;
export function installConsoleMirror(): void {
  if (mirrorInstalled || typeof window === 'undefined' || !window.console) {
    return;
  }
  mirrorInstalled = true;

  const includeNodeModules =
    process.env.NEXT_PUBLIC_MIRROR_NODE_MODULES === 'true';

  // By default we ONLY patch `console.error` — wrapping `warn`/`info`/`debug`
  // makes DevTools attribute every third-party warning (React, Emotion, HMR,
  // etc.) to *this* file instead of the true source, which is confusing.
  // Errors are the high-value case; if you truly want everything mirrored,
  // set NEXT_PUBLIC_MIRROR_ALL_LEVELS=true.
  const levels: LogLevel[] =
    process.env.NEXT_PUBLIC_MIRROR_ALL_LEVELS === 'true'
      ? ['error', 'warn', 'info', 'debug']
      : ['error'];

  for (const level of levels) {
    const orig = originalConsole[level];
    window.console[level] = (...args: unknown[]): void => {
      // Always print to the browser DevTools console.
      try {
        orig(...args);
      } catch {
        /* intentional: never let a broken console method break the app */
      }
      // Then (optionally) mirror to the server.
      try {
        if (!includeNodeModules && !callerIsAppCode()) {
          return;
        }
        const [first, ...rest] = args;
        const message = safeStringify(first);
        const rawCtx =
          rest.length === 0 ? undefined : rest.length === 1 ? rest[0] : rest;
        // Wrap non-plain-object ctx so arrays/errors don't pollute top-level log fields.
        const ctx =
          rawCtx === undefined || rawCtx?.constructor === Object
            ? rawCtx
            : { Context: rawCtx };
        void send(level, message, ctx);
      } catch {
        /* intentional: never let the mirror break the app */
      }
    };
  }
}
